import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "./SectionHeader";
import { useT } from "@/lib/i18n";

export const Pricing = () => {
  const { t } = useT();
  const p = t.pricing;

  return (
    <section id="cennik" className="section-y" aria-labelledby="cennik-title">
      <div className="container-x">
        <SectionHeader kicker={p.kicker} title={p.title} subtitle={p.subtitle} />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Seasons grid */}
          <div className="lg:col-span-3" data-testid="pricing-seasons">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {p.seasons.map((s, idx) => (
                <motion.article
                  key={s.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, ease: "easeOut", delay: idx * 0.05 }}
                  className={`relative rounded-2xl border p-5 shadow-soft transition-[box-shadow,border-color] ${
                    s.featured
                      ? "border-foreground/15 bg-card ring-2 ring-offset-2 ring-offset-background"
                      : "border-border bg-card hover:shadow-lifted"
                  } ${
                    // An odd number of seasons would leave the last one beside a
                    // hole, so it takes the whole final row instead.
                    idx === p.seasons.length - 1 && p.seasons.length % 2 === 1 ? "sm:col-span-2" : ""
                  }`}
                  style={s.featured ? { "--tw-ring-color": "hsl(var(--wood))" } : {}}
                  data-testid={`pricing-season-${idx}`}
                >
                  {s.featured && (
                    <div
                      className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground shadow-soft"
                      style={{ background: "hsl(var(--wood))" }}
                    >
                      <Sparkles className="h-3 w-3" />
                      Populárne
                    </div>
                  )}
                  <div className="kicker">{s.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{s.period}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-semibold text-foreground">{s.price}</span>
                    <span className="text-sm text-muted-foreground">{p.perNight}</span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {p.minStayLabel}: <span className="text-foreground font-medium">{s.minNights}</span>
                  </div>
                </motion.article>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>{p.disclaimer}</p>
            </div>
          </div>

          {/* CTA */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl p-6 shadow-soft text-primary-foreground" style={{ background: "hsl(var(--pine))" }}>
              <div className="kicker-on-dark">{p.kicker}</div>
              <h3 className="mt-2 font-display text-xl font-semibold">{p.askLabel}</h3>
              <p className="mt-2 text-sm text-white/85">
                {t.contact.quickCallBody}
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Button asChild size="lg" variant="secondary" className="h-11 rounded-xl bg-white text-primary hover:bg-white/90 flex-1" data-testid="pricing-primary-cta">
                  <a href="#kontakt" className="inline-flex items-center justify-center gap-1.5">
                    {p.ctaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
